import { useState } from "react";
import { Play, X, ChevronLeft, ChevronRight, Package } from "lucide-react";
import { fileUrl } from "../lib/api";

export const MediaGallery = ({ media = [], fallbackPath = null, aspect = "aspect-[4/3]" }) => {
  // Normalize: prefer media array; if empty but fallbackPath (legacy photo_path), treat as image
  const list =
    media && media.length > 0
      ? media
      : fallbackPath
      ? [{ path: fallbackPath, type: "image" }]
      : [];

  const [lightbox, setLightbox] = useState(null); // index

  if (list.length === 0) {
    return (
      <div className={`w-full ${aspect} rounded-md border border-border bg-secondary flex items-center justify-center`}>
        <Package className="h-20 w-20 text-muted-foreground/30" strokeWidth={1.2} />
      </div>
    );
  }

  const main = list[0];

  return (
    <>
      <div className="space-y-2">
        <div
          onClick={() => setLightbox(0)}
          className={`relative w-full ${aspect} rounded-md overflow-hidden border border-border bg-muted cursor-zoom-in group`}
        >
          {main.type === "image" ? (
            <img
              src={fileUrl(main.path)}
              alt=""
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <video
              src={fileUrl(main.path)}
              className="w-full h-full object-cover"
              controls
              playsInline
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
        {list.length > 1 && (
          <div className="grid grid-cols-5 gap-2">
            {list.slice(1, 6).map((m, i) => (
              <button
                key={i + 1}
                onClick={() => setLightbox(i + 1)}
                className="relative aspect-square rounded-md overflow-hidden border border-border bg-muted hover:border-foreground/30 transition-colors"
              >
                {m.type === "image" ? (
                  <img src={fileUrl(m.path)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <>
                    <video src={fileUrl(m.path)} className="w-full h-full object-cover" muted playsInline />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <Play className="h-4 w-4 text-white ml-0.5" fill="currentColor" />
                    </div>
                  </>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {lightbox !== null && (
        <Lightbox
          items={list}
          index={lightbox}
          onClose={() => setLightbox(null)}
          setIndex={setLightbox}
        />
      )}
    </>
  );
};

function Lightbox({ items, index, onClose, setIndex }) {
  const current = items[index];
  const prev = () => setIndex((index - 1 + items.length) % items.length);
  const next = () => setIndex((index + 1) % items.length);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="media-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 h-10 w-10 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center"
      >
        <X className="h-5 w-5" />
      </button>
      {items.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            className="absolute left-6 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            className="absolute right-6 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}
      <div className="max-w-5xl max-h-[85vh] w-full" onClick={(e) => e.stopPropagation()}>
        {current.type === "image" ? (
          <img
            src={fileUrl(current.path)}
            alt=""
            className="w-full h-full object-contain max-h-[85vh]"
          />
        ) : (
          <video
            src={fileUrl(current.path)}
            className="w-full h-full max-h-[85vh]"
            controls
            autoPlay
            playsInline
          />
        )}
      </div>
    </div>
  );
}
